import React, { useEffect, useCallback, useRef } from "react";
import {
  Button,
  Box,
  Text,
  IconButton,
  VStack,
  HStack,
  Flex,
  Stack,
} from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import { FaCrown } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { RootState } from "../../store";
import { hideToast } from "../../store/freeTrialToast";
import useViewPortWidth from "src/utils/useViewPortWidth";

interface ReduxToastProps {
  className?: string;
}

const ReduxToast: React.FC<ReduxToastProps> = ({ className = "" }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const toastRef = useRef<HTMLDivElement>(null);
  const { viewPortWidth } = useViewPortWidth();

  const { isVisible, message, route, routeLabel, autoClose } = useSelector(
    (state: RootState) => state.freeTrialToast
  );

  const handleClose = useCallback(() => {
    dispatch(hideToast());
  }, [dispatch]);

  const handleMaybeLater = useCallback(() => {
    dispatch(hideToast());
  }, [dispatch]);

  // Handle outside click
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element;
      if (toastRef.current && target && !toastRef.current.contains(target)) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isVisible, handleOutsideClick]);

  useEffect(() => {
    if (autoClose && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [autoClose, isVisible, handleClose]);

  if (!isVisible) return null;

  return (
    <>
      {/* Background Overlay */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="rgba(0, 0, 0, 0.4)"
        zIndex="9998"
        onClick={handleClose}
      />
      
      {/* Toast Content */}
      <Box
        ref={toastRef}
        role="alert"
        aria-live="polite"
        position="fixed"
        top={{ base: "50%", md: "auto" }}
        bottom={{ base: "auto", md: "20px" }}
        left="50%"
        transform={{ base: "translate(-50%, -50%)", md: "translateX(-50%)" }}
        zIndex="9999"
        bg="white"
        borderRadius="20px"
        boxShadow="0 20px 40px 0 rgba(0, 0, 0, 0.1)"
        p="8"
        maxW="320px"
        minW={{ base: "calc(100vw - 32px)", sm: "380px" }}
        mx="auto"
        className={className}
        fontFamily="Calibri (Body)"
      >
        {/* Close Button */}
        {/* @ts-ignore */}
        <IconButton
          aria-label={formatMessage({ id: "close" })}
          icon={<MdClose />}
          size="sm"
          variant="ghost"
          position="absolute"
          top="4"
          right="4"
          h="32px"
          w="32px"
          minW="32px"
          color="gray.500"
          _hover={{ bg: "gray.100", color: "gray.700" }}
          onClick={handleClose}
          cursor="pointer"
          borderRadius="8px"
        />

        <VStack spacing="6" align="stretch">
          {/* Header with Icon and Title */}
          <Stack
            direction={viewPortWidth >= 370 ? "row" : "column"}
            spacing="3"
            align="center"
          >
            <Box
              bg="#1a3b85"
              borderRadius="full"
              p="3"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FaCrown color="white" size="20px" />
            </Box>
            <Text
              fontSize="20px"
              fontWeight="bold"
              color="gray.800"
              fontFamily="Calibri (Body)"
            >
              {formatMessage({ id: "freeTrial.title" })}
            </Text>
          </Stack>

          {/* Subtitle */}
          <Text
            fontSize="16px"
            color="gray.600"
            lineHeight="1.5"
            fontFamily="Calibri (Body)"
            textAlign="left"
          >
            {message || formatMessage({ id: "freeTrial.subtitle" })}
          </Text>

          {/* Buttons */}
          <Flex direction={{ base: "column", md: "row" }} gap="3" align="stretch">
            <Button
              bg="#1a3b85"
              color="white"
              size="lg"
              h="48px"
              borderRadius="12px"
              _hover={{ bg: "#143168" }}
              _active={{ bg: "#0f2654" }}
              fontFamily="Calibri (Body)"
              fontWeight="bold"
              fontSize="16px"
              leftIcon={
                <Box as="span" fontSize="14px">
                  ✨
                </Box>
              }
              flex={{ base: "none", md: "1" }}
              onClick={() => {
                console.log("Upgrade button clicked, route:", route);
                if (route) {
                  window.location.href = route;
                }
              }}
            >
              {routeLabel || formatMessage({ id: "freeTrial.upgradeButton" })}
            </Button>

            <Button
              variant="ghost"
              color="gray.600"
              size="lg"
              h="48px"
              borderRadius="12px"
              _hover={{ bg: "gray.100", color: "gray.700" }}
              _active={{ bg: "gray.200" }}
              fontFamily="Calibri (Body)"
              fontSize="16px"
              flex={{ base: "none", md: "1" }}
              onClick={handleMaybeLater}
            >
              {formatMessage({ id: "freeTrial.maybeLater" })}
            </Button>
          </Flex>
        </VStack>
      </Box>
    </>
  );
};

export default ReduxToast;
